// src/components/Reports/Hesabat.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  DatePicker,
  Button,
  Card,
  Spin,
  Table,
  Divider,
  message,
  Empty,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Select,
  InputNumber,
  Tag,
  Input,
  Space,
} from 'antd';
import {
  SyncOutlined,
  DollarCircleOutlined,
  UserOutlined,
  TeamOutlined,
  PrinterOutlined,
  GiftOutlined,
  CreditCardOutlined,
  QuestionCircleOutlined,
  EditOutlined,
  ClockCircleOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import 'dayjs/locale/az';
import locale from 'antd/es/date-picker/locale/az_AZ';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const CONN_KEY_SENAT = process.env.REACT_APP_NOT_SECRET_CODE_R;

/* =========================================================
   1) ÇAP GÖRÜNÜŞÜ (PrintableView)
========================================================= */
const PrintableView = React.forwardRef(({ reportData, dates }, ref) => {
  if (!reportData || !dates) return null;

  const { summary, details } = reportData;

  const deductionPerCard = 10;

  const redTextStyle = {
    color: 'red',
    fontFamily: 'Arial, sans-serif',
    margin: '8px 0',
    fontSize: '18px',
    fontWeight: 'bold',
  };
  const blackTextStyle = {
    fontFamily: 'Arial, sans-serif',
    margin: '8px 0',
    fontSize: '18px',
    color: 'black',
  };
  const underlineStyle = { textDecoration: 'underline', textDecorationStyle: 'dotted' };

  const fmt = (n) => Number(n || 0).toFixed(2);

  // ---- Kart yenilənməsi sayına Borc_qaytardı daxil olmasın
  const renewables = (details || []).filter((d) => d.payment_type !== 'Borc_qaytardı');
  const renewCount = renewables.length;

  // ---- Borc_qaytardı (kassaya giriş)
  const borcReturned = (details || []).filter((d) => d.payment_type === 'Borc_qaytardı');
  const borcReturnedCount = borcReturned.length;
  const borcReturnedSum = borcReturned.reduce((s, x) => s + Number(x.price || 0), 0);
  const borcReturnedNet1 = borcReturned.reduce((s, x) => s + (Number(x.price || 0) - deductionPerCard), 0);

  // ---- Nağd kartlar (Üzv/Bələdçi hesabı üçün)
  const cashPaid = (details || []).filter((d) => d.payment_type === 'Nağd' && Number(d.price || 0) > 0);

  // Qruplama (Üzv/Bələdçi qiymətə görə)
  const groupedByTypeAndPrice = cashPaid.reduce((acc, item) => {
    const price = fmt(item.price);
    const key = `${item.type}_${price}`;
    if (!acc[key]) acc[key] = { type: item.type, price, count: 0 };
    acc[key].count++;
    return acc;
  }, {});
  const groupedArray = Object.values(groupedByTypeAndPrice);

  // Kart yekunu (yalnız Nağd kartlar, hər kartdan -10)
  const netGroupTotal = groupedArray.reduce((sum, g) => {
    const p = Number(g.price || 0);
    return sum + g.count * (p - deductionPerCard);
  }, 0);

  // Kassa yekunu = Kart yekunu + Borc_qaytardı (price olduğu kimi)
  const cashFinalTotal = netGroupTotal + borcReturnedNet1;

  // 2-ci səhifə üçün Üzv/Bələdçi cəmləri (Nağd)
  const norm = (s) => (s ?? '').toString().trim().toLowerCase().normalize('NFC');
  const isUzv = (t) => norm(t).includes('üzv');
  const isBeledci = (t) => norm(t).includes('bələdçi') || norm(t).includes('surucu') || norm(t).includes('sürücü');

  const cashUzv = cashPaid.filter((d) => isUzv(d.type));
  const cashBeledci = cashPaid.filter((d) => isBeledci(d.type));

  const cashUzvCount = cashUzv.length;
  const cashBeledciCount = cashBeledci.length;

  const cashUzvAmount = cashUzv.reduce((s, x) => s + Number(x.price || 0), 0);
  const cashBeledciAmount = cashBeledci.reduce((s, x) => s + Number(x.price || 0), 0);

  const borcReturnedSum2 = borcReturned.reduce((s, x) => s + Number(x.price || 0), 0);

  // 2-ci səhifə brüt total
  const total2 = cashUzvAmount + cashBeledciAmount + borcReturnedSum2;

  // Z: Nağd + Borc_qaytardı sayına görə
  const z2 = (cashPaid.length + borcReturned.length) * deductionPerCard;
  const net2 = total2 - z2;
  // ---- Special list (Hədiyyə/Borc/Bərpa/Brak/Personal/Borc_qaytardı)
  const renderSpecialCards = (type, page = 1) => {
    const filteredDetails = (details || []).filter((d) => d.payment_type === type);
    if (filteredDetails.length === 0) return null;

    const calcPriceForLine = (item) => {
      const p = Number(item.price || 0);

      // yalnız Borc_qaytardı üçün:
      // 1-ci səhifədə "kart hesabına" daxil deyil, amma kassaya girişdir.
      // Sən "qeyd hissəsində -10" istəmirdin, kassaya giriş dedin -> burada NORMAL göstəririk.
      // 2-ci səhifədə də normal.
      return fmt(p);
    };

    return (
      <>
        <h3 style={redTextStyle}>
          <span style={underlineStyle}>
            {type}: {filteredDetails.length}
          </span>
        </h3>

        <div style={{ marginLeft: '20px', fontSize: '16px' }}>
          {filteredDetails.map((item, index) => (
            <p key={`${type}-${index}`} style={{ ...redTextStyle, fontWeight: 'normal' }}>
              ({item.type} {item.duration_key}-ay) {item.username} {item.surename}
              {' — '}qiymət: {calcPriceForLine(item)} AZN
              {' — '}səbəb: {item.reason || 'qeyd edilməyib'}
            </p>
          ))}
        </div>
      </>
    );
  };

  return (
    <div ref={ref}>
      {/* ========= BİRİNCİ SƏHİFƏ ========= */}
      <div className="printable-page" style={{ padding: '40px' }}>
        <h3 style={redTextStyle}>
          <span style={underlineStyle}>{renewCount} Kart Yenilendi</span>
        </h3>

        {/* Borc_qaytardı yenilənmə deyil, amma kassaya girişdir — ayrıca göstər */}


        {renderSpecialCards('Hədiyyə', 1)}
        {renderSpecialCards('Borc', 1)}
        {renderSpecialCards('Bərpa', 1)}
        {renderSpecialCards('Brak', 1)}
        {renderSpecialCards('Personal', 1)}

        {cashPaid.length > 0 && (
          <p style={redTextStyle}>
            <span style={underlineStyle}>{cashPaid.length} Kart nağd</span>
          </p>
        )}

        <br />

        {/* Üzv/Bələdçi hesab hissəsi (yalnız Nağd) */}
        {groupedArray.map((group, index) => (
          <p key={index} style={blackTextStyle}>
            {group.type} {group.count}x{(Number(group.price) - deductionPerCard).toFixed(2)} ={' '}
            {(group.count * (Number(group.price) - deductionPerCard)).toFixed(2)}
          </p>
        ))}

        <br />

        {/* Kart yekunu (yalnız kart satış/yenilənmə) */}
        <h4 style={redTextStyle}>Yekun = {netGroupTotal.toFixed(2)}</h4>

        {/* Kassaya giriş (borc qaytarı) əlavə olunur */}
        {borcReturnedCount > 0 && (
          <div style={{ marginTop: 22 }}>
            {borcReturned.map((item, index) => (
              <p key={`br2-${index}`} style={redTextStyle}>
                Borc_qaytardı: 1 ({item.type} {item.duration_key}-ay) {item.username} {item.surename}
                + {(Number(item.price || 0) - deductionPerCard).toFixed(2)}
              </p>
            ))}
            <h4 style={redTextStyle}>Yekun = {cashFinalTotal.toFixed(2)}</h4>
          </div>
        )}
      </div>

      {/* ========= SƏHİFƏ AYIRICI ========= */}
      <div className="page-break" />

      {/* ========= İKİNCİ SƏHİFƏ (Z-HESABAT) ========= */}
      <div className="printable-page" style={{ padding: '100px' }}>
        <h3 style={redTextStyle}>
          <span style={underlineStyle}>{renewCount} Kart Yenilendi</span>
        </h3>
        <br />

        {renderSpecialCards('Hədiyyə', 2)}
        {renderSpecialCards('Borc', 2)}

        {renderSpecialCards('Bərpa', 2)}
        {renderSpecialCards('Brak', 2)}
        {renderSpecialCards('Personal', 2)}

        {cashPaid.length > 0 && (
          <p style={redTextStyle}>
            <span style={underlineStyle}>{cashPaid.length} Kart nağd</span>
          </p>
        )}

        <p style={blackTextStyle}>
          Üzv {cashUzvCount} = {cashUzvAmount.toFixed(2)}
        </p>
        <p style={blackTextStyle}>
          Bələdçi {cashBeledciCount} = {cashBeledciAmount.toFixed(2)}
        </p>
        <br />
        {borcReturned.length > 0 && (
  <>
    <h3 style={redTextStyle}><span style={underlineStyle}>Borc_qaytardı: {borcReturned.length}</span></h3>
    <div style={{ marginLeft: '20px', fontSize: '16px' }}>
      {borcReturned.map((item, index) => (
        <p key={`br2page-${index}`} style={{ ...redTextStyle, fontWeight: 'normal' }}>
          ({item.type} {item.duration_key}-ay) {item.username} {item.surename}
          {' — '} + {Number(item.price || 0).toFixed(2)}
        </p>
      ))}
    </div>
  </>
)}

        <p style={redTextStyle}>Yekun = {total2.toFixed(2)}</p>
        <br />
        <p style={redTextStyle}>Z-{z2.toFixed(2)}</p>
        <br />
        <p style={redTextStyle}>Yekun= {net2.toFixed(2)}</p>
      </div>
    </div>
  );
});

/* =========================================================
   2) DASHBOARD (üst statistik kartlar)
========================================================= */
const ReportDashboard = ({ summary }) => (
  <>
    <Row gutter={[10, 10]}>
      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Cəmi" value={summary.totalRenewals} prefix={<SyncOutlined />} />
        </Card>
      </Col>
      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Üzv" value={summary.uzvCount} prefix={<UserOutlined />} />
        </Card>
      </Col>
      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Bələdçi" value={summary.beledciCount} prefix={<TeamOutlined />} />
        </Card>
      </Col>
      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Nağd" value={summary.cashCount} prefix={<CreditCardOutlined />} />
        </Card>
      </Col>
      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Hədiyyə" value={summary.giftCount} prefix={<GiftOutlined />} />
        </Card>
      </Col>
      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Borc" value={summary.debtCount} prefix={<QuestionCircleOutlined />} />
        </Card>
      </Col>

      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Borc_qaytardı" value={summary.debtReturnedCount || 0} prefix={<QuestionCircleOutlined />} />
        </Card>
      </Col>

      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Personal" value={summary.personalCount || 0} prefix={<QuestionCircleOutlined />} />
        </Card>
      </Col>

      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="3 Aylıq" value={summary.threeMonthCount} prefix={<ClockCircleOutlined />} />
        </Card>
      </Col>
      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="6 Aylıq" value={summary.sixMonthCount} prefix={<ClockCircleOutlined />} />
        </Card>
      </Col>

      <Col xs={10} sm={6} md={4} lg={2}>
        <Card>
          <Statistic title="Brak" value={summary.brakCount || 0} prefix={<QuestionCircleOutlined />} />
        </Card>
      </Col>
    </Row>

    <Row style={{ marginTop: '16px' }}>
      <Col span={24}>
        <Card style={{ background: '#e6f4ff' }}>
          <Statistic
            title="Yekun Məbləğ"
            value={summary.totalAmount}
            suffix="AZN"
            prefix={<DollarCircleOutlined />}
            valueStyle={{ color: '#1677ff', fontSize: '24px' }}
          />
        </Card>
      </Col>
    </Row>
  </>
);

/* =========================================================
   3) REDAKTƏ MODALI
========================================================= */
const EditModal = ({ record, isVisible, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [isPriceDisabled, setIsPriceDisabled] = useState(false);
  const [showReasonField, setShowReasonField] = useState(false);

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        payment_type: record.payment_type,
        price: record.price,
        reason: record.reason,
      });
      const type = record.payment_type;
      setIsPriceDisabled(type === 'Hədiyyə' || type === 'Bərpa');
      setShowReasonField(
        type === 'Hədiyyə' ||
        type === 'Bərpa' ||
        type === 'Borc' ||
        type === 'Borc_qaytardı' ||
        type === 'Brak' ||
        type === 'Personal'
      );
    }
  }, [record, form]);

  const handleValuesChange = (changedValues) => {
    if (changedValues.payment_type) {
      const type = changedValues.payment_type;
      setShowReasonField(
        type === 'Hədiyyə' ||
        type === 'Bərpa' ||
        type === 'Borc' ||
        type === 'Borc_qaytardı' ||
        type === 'Brak' ||
        type === 'Personal'
      );
      setIsPriceDisabled(type === 'Hədiyyə' || type === 'Bərpa');

      if (type === 'Hədiyyə' || type === 'Bərpa') {
        form.setFieldsValue({ price: 0 });
      }
      if (type === 'Nağd') {
        form.setFieldsValue({ reason: '' });
      }
    }
  };

  return (
    <Modal
      title="Əməliyyata Düzəliş Et"
      open={isVisible}
      onOk={() => form.validateFields().then((values) => onSave(record, values))}
      onCancel={onCancel}
      okText="Yadda Saxla"
      cancelText="Ləğv Et"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Form.Item name="payment_type" label="Ödəniş Növü" rules={[{ required: true }]}>
          <Select>
            <Option value="Nağd">Nağd</Option>
            <Option value="Hədiyyə">Hədiyyə</Option>
            <Option value="Borc">Borc</Option>
            <Option value="Borc_qaytardı">Borc qaytarı</Option>
            <Option value="Bərpa">Bərpa</Option>
            <Option value="Brak">Brak</Option>
            <Option value="Personal">Personal</Option>
          </Select>
        </Form.Item>

        <Form.Item name="price" label="Qiymət (AZN)" rules={[{ required: true }]}>
          <InputNumber min={0} style={{ width: '100%' }} disabled={isPriceDisabled} />
        </Form.Item>

        {showReasonField && (
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.payment_type !== cur.payment_type}>
            {({ getFieldValue }) => {
              const t = getFieldValue('payment_type');
              const needReason = ['Hədiyyə', 'Bərpa', 'Borc', 'Borc_qaytardı', 'Brak', 'Personal'].includes(t);
              if (!needReason) return null;

              return (
                <Form.Item
                  name="reason"
                  label="Səbəb / Qeyd"
                  preserve={true}
                  rules={[{ required: true, message: 'Qeyd yazmaq məcburidir. Səbəbin aydin formada qeyd edin' }]}
                >
                  <Input.TextArea rows={2} />
                </Form.Item>
              );
            }}
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

/* =========================================================
   4) ƏSAS KOMPONENT
========================================================= */
const Hesabat = () => {
  const [dates, setDates] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const printComponentRef = useRef();

  /* ---------- PDF yönləndirmə setup ---------- */
  const USE_BACKEND_PDF = true;
  const PDF_GUIDE_URL = `/PDF/`;
  const PDF_UZV_URL = `/PDFUzv/`;
  const PDF_GUIDE_ROUTE = (id) => `/PDF/${encodeURIComponent(id)}`;
  const PDF_UZV_ROUTE = (id) => `/PDFUzv/${encodeURIComponent(id)}`;

  const norm = (s) => (s ?? '').toString().trim().toLowerCase().normalize('NFC');
  const isGuideOrDriver = (t) => {
    const v = norm(t);
    return v === 'bələdçi' || v === 'sürücü';
  };

  const openPdf = (record = {}) => {
    const userId = record?.userid ?? record?.user_id ?? record?.id;
    if (!userId) {
      message.warning('PDF üçün istifadəçi ID tapılmadı.');
      return;
    }
    const isGuide = isGuideOrDriver(record?.type);
    if (USE_BACKEND_PDF) {
      const url = (isGuide ? PDF_GUIDE_URL : PDF_UZV_URL) + encodeURIComponent(userId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      const route = isGuide ? PDF_GUIDE_ROUTE(userId) : PDF_UZV_ROUTE(userId);
      window.open(route, '_blank', 'noopener,noreferrer');
    }
  };

  /* ---------- Serverdən hesabatı çək ---------- */
  const handleFetchReport = async () => {
    if (!dates || !dates[0] || !dates[1]) {
      message.warning('Zəhmət olmasa, hesabat üçün başlanğıc və son tarix seçin.');
      return;
    }
    setLoading(true);
    setReportData(null);

    const [startDate, endDate] = dates;

    try {
      const response = await axios.get(`${CONN_KEY_SENAT}GetReport.php`, {
        params: {
          startDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
          endDate: endDate.format('YYYY-MM-DD HH:mm:ss'),
        },
      });

      if (response.data && response.data.status === 1) {
        const details = response.data.details || [];
        const brakCount = details.filter((d) => d.payment_type === 'Brak').length;
        const personalCount = details.filter((d) => d.payment_type === 'Personal').length;
        const debtReturnedCount = details.filter((d) => d.payment_type === 'Borc_qaytardı').length;

        setReportData({
          ...response.data,
          summary: {
            ...response.data.summary,
            brakCount,
            personalCount,
            debtReturnedCount,
          },
        });
      } else {
        message.error(response.data?.message || 'Xəta baş verdi.');
      }
    } catch (error) {
      message.error('Serverlə əlaqə zamanı xəta baş verdi.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const showEditModal = (record) => {
    setEditingRecord(record);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
  };

  const handleModalSave = async (record, values) => {
    const payload = {
      id: record.id,
      payment_type: values.payment_type,
      price: values.price,
      reason: (values.reason ?? '').toString().trim(),
    };
    try {
      const response = await axios.post(`${CONN_KEY_SENAT}UpdateReportEntry.php`, payload);
      if (response.data && response.data.status === 1) {
        message.success(response.data.message);
        setIsModalVisible(false);
        setEditingRecord(null);
        handleFetchReport();
      } else {
        message.error(response.data.message || 'Yeniləmə zamanı xəta baş verdi.');
      }
    } catch (error) {
      message.error('Serverlə əlaqə zamanı xəta baş verdi.');
    }
  };

  /* ---------- Cədvəl köməkçiləri ---------- */
  const getPaymentTag = (type, record = {}) => {
    const colors = {
      Nağd: 'green',
      Hədiyyə: 'blue',
      Borc: 'volcano',
      Bərpa: 'purple',
      Brak: 'red',
      Personal: 'yellow',
      Borc_qaytardı: 'cyan',
    };

    if (type === 'Brak' || type === 'Personal' || type === 'Borc_qaytardı') {
      const color = type === 'Brak' ? 'red' : type === 'Personal' ? 'gold' : 'cyan';
      return (
        <Space size="small">
          <Tag color={color}>
            {type} — {record.type || 'Naməlum'}
          </Tag>
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              openPdf(record);
            }}
          />
        </Space>
      );
    }

    return (
      <Space size="small">
        <Tag color={colors[type] || 'default'}>{type}</Tag>
        <Button
          size="small"
          icon={<FilePdfOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            openPdf(record);
          }}
        />
      </Space>
    );
  };

  const columns = [
    {
      title: 'Ad Soyad',
      dataIndex: 'username',
      render: (_, record) => `${record.username || ''} ${record.surename || ''}`,
    },
    {
      title: 'Ödəniş Növü',
      dataIndex: 'payment_type',
      render: (_, record) => getPaymentTag(record.payment_type, record),
    },
    { title: 'Tarix', dataIndex: 'buy_date', render: (date) => new Date(date).toLocaleDateString('az-AZ'), responsive: ['md'] },
    { title: 'Qiymət', dataIndex: 'price', render: (price) => `${parseFloat(price).toFixed(2)} AZN`, align: 'right' },
    {
      title: 'Düzəliş',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => showEditModal(record)} />
      ),
    },
  ];

  return (
    <>
      <div className="screen-view">
        <Card title="Hesabat Paneli" style={{ margin: '20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
            <RangePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              locale={locale}
              onChange={(values) => setDates(values)}
            />
            <Button type="primary" onClick={handleFetchReport} loading={loading}>
              Hesabatı Göstər
            </Button>
            {reportData && (
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                Çap Et
              </Button>
            )}
          </div>

          <Divider />

          {loading && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          )}

          {!loading && reportData && (
            <>
              <ReportDashboard summary={reportData.summary} />
              <Divider>Əməliyyatların Siyahısı</Divider>
              <Table
                columns={columns}
                dataSource={reportData.details}
                rowKey="id"
                bordered
                size="small"
                pagination={{ pageSize: 10, responsive: true }}
                scroll={{ x: 'max-content' }}
                expandable={{
                  expandedRowRender: (record) => (record.reason ? <p style={{ margin: 0 }}><b>Səbəb:</b> {record.reason}</p> : null),
                  rowExpandable: (record) => !!record.reason,
                }}
              />
            </>
          )}

          {!loading && !reportData && <Empty description="Hesabatı görmək üçün tarix seçin." />}
        </Card>
      </div>

      <div className="print-view">
        <PrintableView ref={printComponentRef} reportData={reportData} dates={dates} />
      </div>

      <EditModal isVisible={isModalVisible} record={editingRecord} onSave={handleModalSave} onCancel={handleModalCancel} />
    </>
  );
};

export default Hesabat;
